import { Capacitor } from '@capacitor/core';
import { SecurePreferences } from '@capawesome-team/capacitor-secure-preferences';

import { environment } from '../../environment/environment';
import { isNative } from '../../helpers/utils.helper';
import { CryptoService } from '../../services/crypto.service';
import { RaygunErrorHandlerService } from '../../services/raygun.service';
import type { AppDispatch } from '../../store';
import { clearAllStates } from '../../store';
import { showTokenTimeOut } from '../tabs/tabsSlice';
import { AuthResult, removeVaultValue } from './loginSlice';

const { resetRaygunUser, logError } = RaygunErrorHandlerService();

export const didOnboard = () => {
  return localStorage.getItem('onboard') === 'true';
};

export const getWebUserAsync = async () => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return new AuthResult();
  }

  const ret = await SecurePreferences.get({ key: environment.authResultKey });
  const cryptedAuthResult = ret.value;
  if (cryptedAuthResult) {
    try {
      const decrypted = await CryptoService.decrypt(cryptedAuthResult, environment.cryptoKey, 'getWebUserAsync');
      const authResult: AuthResult = JSON.parse(decrypted);
      return authResult?.token ? authResult : new AuthResult();
    } catch (error) {
      logError(error, ['Login.servive', 'getWebUserAsync']);
      return new AuthResult();
    }
  }
  return new AuthResult();
};

export const removeWebUser = async () => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  await SecurePreferences.remove({ key: environment.authResultKey });
};

export const getBiometricsEmailAsync = async () => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return '';
  }

  const ret = await SecurePreferences.get({ key: environment.biometricsEmailKey });
  const cryptedEmail = ret.value;
  if (cryptedEmail) {
    const decryptedEmail = CryptoService.decrypt(cryptedEmail, environment.cryptoKey, 'getBiometricsEmailAsync');
    return decryptedEmail;
  }
  return '';
};

export const removeBiometricsEmail = async () => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  await SecurePreferences.remove({ key: environment.biometricsEmailKey });
};

export const setOnboard = () => {
  localStorage.setItem('onboard', 'true');
};

export const logout = async (dispatch: AppDispatch, is401?: boolean) => {
  try {
    await dispatch(removeVaultValue(true));
    await removeWebUser();
    localStorage.removeItem('policySet');
    resetRaygunUser();
    const { removeAllOfflineData } = await import('../../services/offlineJournal.service');
    await removeAllOfflineData();
    dispatch(clearAllStates());
  } catch (error) {
    logError(error, ['Login Service', 'logout']);
  }

  if (!isNative() && is401) {
    dispatch(showTokenTimeOut());
  }
};

export const saveRememberEmail = async (email: string) => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  const encrypted = await CryptoService.encrypt(email, environment.cryptoKey, 'saveRememberEmail');
  await SecurePreferences.set({
    key: environment.rememberEmailKey,
    value: encrypted,
  });
};

export const getRememberEmail = async (): Promise<string | null> => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return null;
  }

  const encryptedEmail = await SecurePreferences.get({ key: environment.rememberEmailKey });

  if (encryptedEmail.value) {
    const decrypted = await CryptoService.decrypt(encryptedEmail.value, environment.cryptoKey, 'getRememberEmail');
    return decrypted;
  } else {
    return null;
  }
};

export const removeRememberEmail = async () => {
  if (!Capacitor.isPluginAvailable('Preferences')) {
    return;
  }

  await SecurePreferences.remove({ key: environment.rememberEmailKey });
};
